import { defineStore } from 'pinia'
import sortBy from 'lodash/sortBy'
import findIndex from 'lodash/findIndex'
import cloneDeep from 'lodash/cloneDeep'
import remove from 'lodash/remove'
import { FolderApi, DocumentApi, TrashApi } from '../test-api/index.ts'
import useBcBreadCrumbsStore from './bcBreadCrumbs'
import handleApiError from '../utils/handleApiError'

// ids which has been loaded in folders cache
let loadedHashTable = {
  trash: Object.create(null),
  bc: Object.create(null),
  shared: Object.create(null)
}
let shouldOpenLeftMenu = null
const folderApi = new FolderApi({}, '/api/v2')
const documentApi = new DocumentApi({}, '/api/v2')
const trashApi = new TrashApi({}, '/api/v2')

// initial state
const state = () => ({
  folders: { bc: [], shared: [], trash: [] },
  documents: { bc: [], shared: [], trash: []  },
  current: null, // current folder object
  loading: true,
  leftMenuOpened: Object.create(null), // url => true, hashTable with opened left menu folders
  leftMenuCurrent: Object.create(null), // url => true, hashTable with current path highligted
  view: 'grid', // grid, table
  order: 'rank', // rank, name
  show: 'bc', // bc or shared or trash
  resetNeeded: false, // reset on next folder (except trash) load
  trashContent: null,
  trashId: null,
  trashLoaded: false,
  limit: 50,
  offset: 0
})

// previews recursion
const listFolderPreviewsRecursive = (hashTable, folder) => {
  const categoryPreviews = []
  const previewStack = createPreviewStack(hashTable, folder)
  let maximalRecursion = 0
  do {
    for (let i = 0; i < previewStack.length; i++) {
      const arr = previewStack[i]
      if (arr.length === 0) {
        previewStack.splice(i, 1)
        continue
      }

      if (categoryPreviews.length >= 5) {
        continue
      }

      categoryPreviews.push(arr.pop())
    }
  } while (
    categoryPreviews.length < 5 &&
    previewStack.length > 0 &&
    ++maximalRecursion < 100
    )

  if (categoryPreviews.length) {
    folder.calcPreviews = categoryPreviews
  }
}

const createPreviewStack = (hashTable, folder) => {
  const output = []

  hashTable[folder.id]?.forEach((child) => {
    if (child.type === 'document') {
      if (child.previews.length) {
        output.push([...child.previews].reverse())
      }
    } else {
      //if (!child.previews?.length && !child.calcPreviews?.length) {
      // need to be really recursive
      listFolderPreviewsRecursive(hashTable, child)
      //}

      if (child.previews?.length) {
        output.push([].concat(child.previews))

      } else if (child.calcPreviews?.length) {
        output.push([].concat(child.calcPreviews).reverse())
      }
    }
  })

  if (folder.previews?.length) {
    return [[].concat(folder.previews)]
  }

  return output
}

// functionName: (state, getters, rootState) => {}
const getters = {
  trashBin() {
    return this.show === 'trash'
  },
  listChildrenDocuments() {
    const hashTable = Object.create(null)
    this.documents[this.show].forEach((document) => {
      const parentId = document.bc_folder_id ? document.bc_folder_id : 0

      if (!hashTable[parentId]) hashTable[parentId] = []

      hashTable[parentId].push(document)
    })
    return hashTable
  },
  listChildrenFolders() {
    const hashTable = Object.create(null)
    this.folders[this.show].forEach((folder) => {
      const parentId = folder.parent__bc_folder_id
        ? folder.parent__bc_folder_id
        : 0

      if (!hashTable[parentId]) hashTable[parentId] = []

      hashTable[parentId].push(folder)
    })
    return hashTable
  },
  listChildren() {
    const hashTable = { ...this.listChildrenFolders }

    Object.keys(this.listChildrenDocuments).forEach((parentId) => {
      if (!hashTable[parentId]) hashTable[parentId] = []

      hashTable[parentId] = hashTable[parentId].concat(
        this.listChildrenDocuments[parentId]
      )
    })

    Object.keys(hashTable).forEach((parentId) => {
      hashTable[parentId] = sortBy(hashTable[parentId], [this.order])
    })

    hashTable[0]?.forEach((item) => {
      if (item.type === 'folder') {
        listFolderPreviewsRecursive(hashTable, item)
      }
    })

    return hashTable
  },
  listParents() {
    const hashTable = this.hashTable
    return (childId) => {
      if (!childId || !hashTable[childId]) return []

      const parents = []
      let parentId = hashTable[childId].parent__bc_folder_id
      while (parentId) {
        parents.push(hashTable[parentId])
        parentId = hashTable[parentId].parent__bc_folder_id
      }
      return parents.reverse()
    }
  },
  hashTable() {
    const hashTable = Object.create(null)
    this.folders[this.show].forEach((folder) => {
      hashTable[folder.id] = folder
      hashTable[folder.id].children = []
    })
    return hashTable
  },
  hashTableDocuments() {
    const hashTable = Object.create(null)
    this.documents[this.show].forEach((document) => {
      hashTable[document.id] = document
    })
    return hashTable
  },
  treeFolders() {
    const dataTree = []
    const hashTable = cloneDeep(this.hashTable)
    this.folders[this.show].forEach((folder) => {
      if (folder.parent__bc_folder_id)
        hashTable[folder.parent__bc_folder_id]?.children.push(
          hashTable[folder.id]
        )
      else dataTree.push(hashTable[folder.id])
    })
    return dataTree
  },
  tree() {
    let dataTree = []
    const hashTable = cloneDeep(this.hashTable)
    this.folders[this.show].forEach((folder) => {
      if (folder.parent__bc_folder_id)
        hashTable[folder.parent__bc_folder_id]?.children.push(
          hashTable[folder.id]
        )
      else dataTree.push(hashTable[folder.id])
    })
    if (this.show !== 'trash') {
      this.documents[this.show].forEach((document) => {
        if (document.bc_folder_id && !hashTable[document.bc_folder_id]) return

        if (document.bc_folder_id)
          hashTable[document.bc_folder_id].children.push(document)
        else dataTree.push(document)
      })
    }
    // sort children
    this.folders[this.show].forEach((folder) => {
      if (hashTable[folder.id].children.length) {
        hashTable[folder.id].children = sortBy(hashTable[folder.id].children, [
          this.order
        ])
      }
    })

    return sortBy(dataTree, [this.order])
  }
}

// functionName ({ commit, state, dispatch }, param, ...) {}
const actions = {
  selectTrash(id) {
    this.trashId = id
    this.trashContent = []
    this.offset = 0
    this.trashLoaded = false
    //TODO: cancel running requests?

  },
  async loadTrash(reset = false) {
    this.loading = true
    if (reset) {
      this.trashContent = []
      this.offset = 0
      this.trashLoaded = false
    }
    const response = await handleApiError(() => trashApi.trashList(this.trashId, this.limit, this.offset, this.order === 'rank' ? 'deleted' : this.order))
    this.trashContent = this.trashContent.concat(response.data)
    this.loading = false
    this.trashLoaded = response.data.length < this.limit
  },
  async loadFolder(id) {
    if (this.resetNeeded) {
      this.resetNeeded = false
      this.reset()
    }

    if (loadedHashTable[this.show][id]) return

    this.loading = true
    loadedHashTable[this.show][id] = true
    let foldersListResponse, documentsListResponse

    if (this.show === 'shared') {
      foldersListResponse = handleApiError(() => folderApi.folderListShared(id || undefined))
      documentsListResponse = handleApiError(() => documentApi.documentListShared(
        id || undefined
      ))

    } else if (this.show === 'trash') {
      foldersListResponse = handleApiError(() => folderApi.folderListTrash(id || undefined))
      documentsListResponse = new Promise((resolve) => {
        resolve({ data: [] })
      })
    
    } else {
      foldersListResponse = handleApiError(() => folderApi.folderList(id || undefined))
      documentsListResponse = handleApiError(() => documentApi.documentList(id || undefined))
    }

    foldersListResponse = await foldersListResponse
    documentsListResponse = await documentsListResponse

    this.loadFolders(foldersListResponse.data, documentsListResponse.data)

    if (shouldOpenLeftMenu) {
      this.enter(shouldOpenLeftMenu[0], shouldOpenLeftMenu[1])
    }
  },
  enter(folderId, documentId, documentName, documentUrl) {
    let hashTableDocuments = null
    if (!this.trashBin && documentId) {
      hashTableDocuments = this.hashTableDocuments
      if (!hashTableDocuments[documentId]) {
        shouldOpenLeftMenu = [folderId, documentId, documentName, documentUrl]
        return
      } else {
        folderId = hashTableDocuments[documentId].bc_folder_id
      }
    }

    const hashTable = this.hashTable
    if (
      folderId && (
        !hashTable[folderId]
        || (
          hashTable[folderId].parent__bc_folder_id !== null
          && !hashTable[hashTable[folderId].parent__bc_folder_id]
        )
      )
    ) {
      shouldOpenLeftMenu = [folderId, documentId, documentName, documentUrl]
      return
    }

    this.current = hashTable[folderId]

    const bcBreadCrumbsStore = useBcBreadCrumbsStore()
    shouldOpenLeftMenu = null
    const opened = Object.create(null)
    const current = Object.create(null)
    bcBreadCrumbsStore.reset()

    if (folderId) {
      this.listParents(folderId).forEach((aData) => {
        opened[aData.url] = true
        current[aData.url] = true
        bcBreadCrumbsStore.addCrumb(aData.name, aData.url)
      })
      opened[hashTable[folderId].url] = true
      current[hashTable[folderId].url] = true
      bcBreadCrumbsStore.addCrumb(
        hashTable[folderId].name,
        hashTable[folderId].url
      )
    }

    if (documentId) {
      if (this.trashBin) {
        opened[documentUrl] = true
        current[documentUrl] = true
        bcBreadCrumbsStore.addCrumb(
          documentName,
          documentUrl
        )
      
      } else {
        opened[hashTableDocuments[documentId].url] = true
        current[hashTableDocuments[documentId].url] = true
        bcBreadCrumbsStore.addCrumb(
          hashTableDocuments[documentId].name,
          hashTableDocuments[documentId].url
        )
      }
    }

    this.setLeftMenuOpened(opened, current)
  },
  loadFolders(folders, documents) {
    folders.forEach((folder) => {
      if (folder.parent__bc_folder_id)
        loadedHashTable[this.show][folder.id] = true
    })
    this.folders[this.show] = [].concat(this.folders[this.show], folders)
    this.documents[this.show] = [].concat(this.documents[this.show], documents)
    this.loading = false
  },
  setLeftMenuOpened(opened, current) {
    this.leftMenuOpened = opened
    if (current) {
      this.leftMenuCurrent = current
    }
  },
  setResetNeeded() {
    this.resetNeeded = true
  },
  toggleView() {
    if (this.view === 'grid') {
      this.view = 'table'
    } else {
      this.view = 'grid'
    }
  },
  toggleOrder() {
    if (this.order === 'rank') {
      this.order = 'name'
    } else {
      this.order = 'rank'
    }
  },
  updateFolder(folder) {
    const index = findIndex(this.folders.bc, ['id', folder.id])
    if (index !== -1) {
      this.folders.bc.splice(index, 1)
    }

    this.loadFolders([folder], [])
    if (this.current?.id === folder.id) {
      this.current = this.hashTable[folder.id]
    }
  },
  updateDocument(document) {
    const index = findIndex(this.documents.bc, ['id', document.id])
    if (index !== -1) {
      this.documents.bc.splice(index, 1)
    }

    this.loadFolders([], [document])
  },
  updateDocumentDetail(id, key, val) {
    const index = findIndex(this.documents.bc, ['id', id])
    this.documents.bc[index][key] = val
  },
  updateFolderDetail(id, key, val) {
    const index = findIndex(this.folders.bc, ['id', id])
    this.folders.bc[index][key] = val
  },
  deleteDocument(id) {
    remove(this.trashContent, { id, type: 'document' })
    const index = findIndex(this.documents[this.show], ['id', id])

    if (index === -1) {
      return
    }
    
    const deletedDocument = this.documents[this.show].splice(index, 1)
    const children = this.listChildren[deletedDocument[0].bc_folder_id ?? 0]

    if (!children) { // children can be empty, if there is no children in folder
      return
    }

    for (let rank = 0; rank < children.length; rank++) {
      children[rank].rank = rank
    }
  },
  moveItem(item, newRank, moveToFolderId = null) {
    const parentFolderKey = item.type === 'document' ? 'bc_folder_id' : 'parent__bc_folder_id'

    if ((moveToFolderId || moveToFolderId === 0) && item[parentFolderKey] !== moveToFolderId) {
      const oldFolderChildren = this.listChildren[item[parentFolderKey] ?? 0]
      const oldRank = item.rank

      oldFolderChildren.splice(oldRank, 1)
      
      if (this.listChildren[moveToFolderId]) {
        const newFolderChildren = this.listChildren[moveToFolderId] ?? []
      
        for (
          let rank = newRank; 
          rank < newFolderChildren.length;
          rank++
        ) {
          let hashtableItem

          if (newFolderChildren[rank].type === 'document') {
            hashtableItem = this.hashTableDocuments[newFolderChildren[rank].id]
          } else {
            hashtableItem = this.hashTable[newFolderChildren[rank].id]
          }

          hashtableItem.rank = rank + 1
        }

        let movedHashtableItem
        if (item.type === 'document') {
          movedHashtableItem = this.hashTableDocuments[item.id]
        } else {
          movedHashtableItem = this.hashTable[item.id]
        }
        
        movedHashtableItem[parentFolderKey] = moveToFolderId
        movedHashtableItem.rank = newRank
        
      } else {
        if (item.type === 'document') {
          const movedDocumentIndex = findIndex(this.documents[this.show], { id: item.id })
          this.documents[this.show].splice(movedDocumentIndex, 1)
          delete this.hashTableDocuments[item.id]
        } else {
          const movedFolderIndex = findIndex(this.folders[this.show], { id: item.id })
          this.folders[this.show].splice(movedFolderIndex, 1)
          delete this.hashTable[item.id]
        } 
      }

      for (
        let rank = oldRank; 
        rank < oldFolderChildren.length;
        rank++
      ) {
        let hashtableItem

        if (oldFolderChildren[rank].type === 'document') {
          hashtableItem = this.hashTableDocuments[oldFolderChildren[rank].id]
        } else {
          hashtableItem = this.hashTable[oldFolderChildren[rank].id]
        }

        hashtableItem.rank = rank
      }

      return
    }

    const children = this.listChildren[item[parentFolderKey] ?? 0]
    const oldRank = item.rank
    const direction = newRank > oldRank ? 1 : -1

    children.splice(oldRank, 1)
    children.splice(newRank, 0, item)

    for (
      let rank = oldRank; 
      direction > 0 ? rank <= newRank : rank >= newRank;
      rank += direction
    ) {
      let hashtableItem

      if (children[rank].type === 'folder') {
        hashtableItem = this.hashTable[children[rank].id]
      } else {
        hashtableItem = this.hashTableDocuments[children[rank].id]
      }

      hashtableItem.rank = rank
    }
  },
  deleteFolder(id) {
    if (this.listChildrenFolders[id] && this.listChildrenFolders[id].length) {
      this.listChildrenFolders[id].forEach((folder) => {
        this.deleteFolder(folder.id)
      })
    }

    if (this.listChildrenDocuments[id] && this.listChildrenDocuments[id].length) {
      this.listChildrenDocuments[id].forEach((doc) => {
        this.deleteDocument(doc.id)
      })
    }

    remove(this.trashContent, { id, type: 'folder' })
    const index = findIndex(this.folders[this.show], ['id', id])
    
    if (index === -1) {
      return
    }
    
    const deletedFolder = this.folders[this.show].splice(index, 1)
    const children = this.listChildren[deletedFolder[0].parent__bc_folder_id ?? 0]

    if (!children) { // children can be empty, if there is no children in folder
      return
    }

    for (let rank = 0; rank < children.length; rank++) {
      children[rank].rank = rank
    }
  },
  reset() {
    this.folders = { bc: [], shared: [], trash: [] }
    this.documents = { bc: [], shared: [], trash: [] }
    this.current = null // current folder object
    this.loading = true
    this.leftMenuOpened = Object.create(null) // url => true, hashTable with opened left menu folders
    this.leftMenuCurrent = Object.create(null) // url => true, hashTable with current path highligted

    loadedHashTable = {
      trash: Object.create(null),
      bc: Object.create(null),
      shared: Object.create(null)
    }
  }
}

export default defineStore({
  id: 'bcFolder',
  state,
  getters,
  actions
})
